/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { DetailedInventoryLedgerInfo } from "./info";
import { DetailedInventoryLedgerService } from "services/detailed-inventory-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  year: today.getFullYear().toString(),
  accountNumber: "",
  warehouseName: "",
  itemName: "",
  itemUnit: "",
  details: [
    {
      docNumber: "",
      docDate: "",
      description: "",
      offsetAccount: "",
      unitPrice: 0,
      inwardQuantity: 0,
      inwardAmount: 0,
      outwardQuantity: 0,
      outwardAmount: 0,
      balanceQuantity: 0,
      balanceAmount: 0,
      note: ""
    }
  ],
  totalPages: "",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function DetailedInventoryLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await DetailedInventoryLedgerService.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            year: apiData.year || initialValues.year,
            accountNumber: apiData.accountNumber || "",
            warehouseName: apiData.warehouseName || "",
            itemName: apiData.itemName || "",
            itemUnit: apiData.itemUnit || "",
            details: apiData.details?.length > 0 ? apiData.details : initialValues.details,
            totalPages: apiData.totalPages || "",
            openingDate: apiData.openingDate || "",
            reportDate: apiData.reportDate || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/detailed-inventory-ledger");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        year: fValues.year,
        accountNumber: fValues.accountNumber,
        warehouseName: fValues.warehouseName,
        itemName: fValues.itemName,
        itemUnit: fValues.itemUnit,
        details: (fValues.details || []).map(row => ({
          docNumber: row.docNumber,
          docDate: row.docDate,
          description: row.description,
          offsetAccount: row.offsetAccount,
          unitPrice: num(row.unitPrice),
          inwardQuantity: num(row.inwardQuantity),
          inwardAmount: num(row.inwardAmount),
          outwardQuantity: num(row.outwardQuantity),
          outwardAmount: num(row.outwardAmount),
          balanceQuantity: num(row.balanceQuantity),
          balanceAmount: num(row.balanceAmount),
          note: row.note,
        })),
        totalPages: fValues.totalPages,
        openingDate: fValues.openingDate,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await DetailedInventoryLedgerService.create(payload);
        toast.success("Tạo sổ chi tiết thành công!");
      } else {
        await DetailedInventoryLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/detailed-inventory-ledger");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S10-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <DetailedInventoryLedgerInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Sổ S10-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default DetailedInventoryLedgerDetail;
